import { PaginationType } from '../../types/utils/pagination.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { ReleaseNoteListItemType, ReleaseNote, ReleasePortalType } from '../../types/release-notes/release-notes';
import { createSlice } from '@reduxjs/toolkit';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import {
  deleteReleaseNoteDetails,
  getPortalVersion,
  getReleaseNoteDetails,
  getReleaseNotesList,
  patchReleaseNoteDetails,
  postReleaseNoteDetails,
} from './releaseNotes.action';
import snackbarUtils from '../../utils/SnackbarUtils';

type ReleaseNotesRouteParams = RouteParamsType & {
  status: string;
  visibility: string;
  published: 'all' | 'false' | 'true';
  portal_type: string;
};

export interface ReleaseNotesState {
  releaseNotesList: null | PaginationType<ReleaseNoteListItemType>;
  releaseNotesListFetchStatus: FETCH_STATUS;
  releaseNotesRouteParams: ReleaseNotesRouteParams;
  releaseNoteDetails: null | ReleaseNote;
  releaseNoteDetailsFetchStatus: FETCH_STATUS;
  releaseNoteDetailsPostStatus: FETCH_STATUS;
  releaseNoteDetailsPatchStatus: FETCH_STATUS;
  releaseNoteDetailsDeleteStatus: FETCH_STATUS;

  versionNumber: string | null;
}

export const initialReleaseNotesRouteParams: ReleaseNotesRouteParams = {
  page: 1,
  limit: 10,
  order: '-id',
  q: '',
  status: '',
  visibility: '',
  published: 'all',
  portal_type: ReleasePortalType.TELCLOUD,
};

const INIT_STATE: ReleaseNotesState = {
  releaseNotesRouteParams: initialReleaseNotesRouteParams,
  releaseNotesList: null,
  releaseNotesListFetchStatus: FETCH_STATUS.NULL,
  releaseNoteDetails: null,
  releaseNoteDetailsFetchStatus: FETCH_STATUS.NULL,
  releaseNoteDetailsPostStatus: FETCH_STATUS.NULL,
  releaseNoteDetailsPatchStatus: FETCH_STATUS.NULL,
  releaseNoteDetailsDeleteStatus: FETCH_STATUS.NULL,

  versionNumber: null,
};

const releaseNotesSlice = createSlice({
  name: 'releaseNotes',
  initialState: INIT_STATE,
  reducers: {
    setReleaseNotesList: (state, action) => {
      state.releaseNotesList = action.payload;
    },
    setReleaseNotesListFetchStatus: (state, action) => {
      state.releaseNotesListFetchStatus = action.payload;
    },
    setReleaseNoteDetails: (state, action) => {
      state.releaseNoteDetails = action.payload;
    },
    setReleaseNoteDetailsFetchStatus: (state, action) => {
      state.releaseNoteDetailsFetchStatus = action.payload;
    },
    setReleaseNoteDetailsPostStatus: (state, action) => {
      state.releaseNoteDetailsPostStatus = action.payload;
    },
    setReleaseNoteDetailsPatchStatus: (state, action) => {
      state.releaseNoteDetailsPatchStatus = action.payload;
    },
    setReleaseNoteDetailsDeleteStatus: (state, action) => {
      state.releaseNoteDetailsDeleteStatus = action.payload;
    },
  },

  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'releaseNotes') {
        state.releaseNotesRouteParams = { ...state.releaseNotesRouteParams, ...payload.data };
      }
    });

    // CREATE RELEASE NOTE DETAILS
    builder
      .addCase(postReleaseNoteDetails.pending, state => {
        state.releaseNoteDetailsPostStatus = FETCH_STATUS.PENDING;
      })
      .addCase(postReleaseNoteDetails.rejected, (state, action) => {
        state.releaseNoteDetailsPostStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => snackbarUtils.error(action.payload as string));
      })
      .addCase(postReleaseNoteDetails.fulfilled, state => {
        state.releaseNoteDetailsPostStatus = FETCH_STATUS.FULFILLED;
        setTimeout(() => snackbarUtils.success('Release note created successfully!'));
      });

    // GET RELEASE NOTES LIST
    builder
      .addCase(getReleaseNotesList.pending, state => {
        state.releaseNotesListFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getReleaseNotesList.rejected, (state, action) => {
        state.releaseNotesListFetchStatus = FETCH_STATUS.REJECTED;
        state.releaseNotesList = null;
        setTimeout(() => snackbarUtils.error(action.payload as string));
      })
      .addCase(getReleaseNotesList.fulfilled, (state, action) => {
        state.releaseNotesListFetchStatus = FETCH_STATUS.FULFILLED;
        state.releaseNotesList = action.payload;
      });

    // GET RELEASE NOTES DETAILS
    builder
      .addCase(getReleaseNoteDetails.pending, state => {
        state.releaseNoteDetailsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getReleaseNoteDetails.rejected, (state, action) => {
        state.releaseNoteDetailsFetchStatus = FETCH_STATUS.REJECTED;
        state.releaseNoteDetails = null;
        setTimeout(() => snackbarUtils.error(action.payload as string));
      })
      .addCase(getReleaseNoteDetails.fulfilled, (state, action) => {
        state.releaseNoteDetailsFetchStatus = FETCH_STATUS.FULFILLED;
        state.releaseNoteDetails = action.payload;
      });

    // PATCH RELEASE NOTES DETAILS
    builder
      .addCase(patchReleaseNoteDetails.pending, state => {
        state.releaseNoteDetailsPatchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(patchReleaseNoteDetails.rejected, (state, action) => {
        state.releaseNoteDetailsPatchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => snackbarUtils.error(action.payload as string));
      })
      .addCase(patchReleaseNoteDetails.fulfilled, state => {
        state.releaseNoteDetailsPatchStatus = FETCH_STATUS.FULFILLED;
        setTimeout(() => snackbarUtils.success('Release note updated successfully!'));
      });

    // DELETE RELEASE NOTES DETAILS
    builder
      .addCase(deleteReleaseNoteDetails.pending, state => {
        state.releaseNoteDetailsDeleteStatus = FETCH_STATUS.PENDING;
      })
      .addCase(deleteReleaseNoteDetails.rejected, (state, action) => {
        state.releaseNoteDetailsDeleteStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => snackbarUtils.error(action.payload as string));
      })
      .addCase(deleteReleaseNoteDetails.fulfilled, state => {
        state.releaseNoteDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
        setTimeout(() => snackbarUtils.success('Release note deleted successfully!'));
      });

    builder.addCase(getPortalVersion.fulfilled, (state, action) => {
      state.versionNumber = action.payload.versionNumber;
    });
  },
});

export const {
  setReleaseNotesList,
  setReleaseNoteDetailsPostStatus,
  setReleaseNoteDetailsPatchStatus,
  setReleaseNotesListFetchStatus,
  setReleaseNoteDetailsDeleteStatus,
  setReleaseNoteDetailsFetchStatus,
  setReleaseNoteDetails,
} = releaseNotesSlice.actions;

export default releaseNotesSlice.reducer;
